import paper from "paper";
import { ToolBase } from "./ToolBase";
import { computeDefaultSmoothAnchors } from "../model/constructionAnchors";
import type { PointerSample } from "../canvas/PointerInput";
import { cssVar } from "../colors";
import { newId } from "../model/ids";
import { withStitchLine } from "../model/projectOps";
import { newSeamLayoutDefaults } from "../model/stitchLayout";
import { pathFromBuffer, type Scene } from "../canvas/Scene";
import {
  currentPointerKind,
  hasHover,
  hitRadiusFor,
} from "../canvas/pointerPrecision";
import { DrawGestureEngine, type DrawGestureHost } from "./drawGestureEngine";
import {
  isVisible,
  nextStitchName,
  pieceForStitchLine,
  resolvePartStitchingDefaults,
  resolveProjectStitchingDefaults,
  type StitchLine,
  type StitchRef,
  type Vec2,
} from "../model/Project";
import { stitchMatchPick } from "../ui/stitchMatchPick";
import { showCanvasToast } from "../canvasToast";
import {
  effectivePitchFromDefaults,
  stitchHolePlacements,
  stitchHolePathData,
} from "../model/stitching";
import {
  angleModifierHeld,
  constrainSquare,
  isAngleSnapActive,
  isCenterSnapActive,
  isSizesSnapActive,
  resolveDragAxis,
  resolveDragBox,
  snapToCommonAngle,
  type Point2,
} from "./dragSnap";
import {
  gatherDrawSnapTargets,
  resolveDrawSnap,
  type DrawSnapResult,
  type DrawSnapTargets,
} from "./drawSnap";
import { makeSlotPathData } from "./CutoutTool";
import { resolveDrawTarget, toOriginalSpace } from "./drawTarget";
import { pieceDisplayOffset, translatePathData } from "../model/pieceTransform";
import { setAngleHint } from "../snapAngleIndicator";
import {
  RegularPolygonGesture,
  type PolygonPreviewShape,
} from "./regularPolygonGesture";
import {
  CLOSE_SNAP_RADIUS,
  closeSnapRadius,
  emitSegmentRay,
} from "./drawGesture";
import { translateStitchLineInProject } from "./SelectTool";
import {
  distanceToEdge,
  nearestPointOnEdge,
  offsetSign,
  nearestOutlineEdge,
  offsetSegmentToPoints,
  offsetWholeToPoints,
  offsetHoverGeometry,
} from "../model/stitchOffset";
import { clipSeamToOutline, cropSeamForPiece } from "../model/stitchCrop";
import {
  seamFromPathData,
  seamPathData,
  type SeamHandle,
} from "../model/seamGeometry";
import {
  DEFAULT_LEATHER_SMOOTHNESS,
  smoothSeamFromRawPoints,
} from "../model/leatherSmoothing";

/** Stitch seam kinds. `offset` (#768, the default) drags a seam parallel to a
 *  part edge. Click-to-add: `polyline` (open), `polygon` (closed) and `line`
 *  (one segment, #1113). Drag-out: the closed shapes `rect` / `ellipse` /
 *  `slot` (#766). Mirrors the construction / shape tools. */
export type StitchKind =
  | "offset"
  | "polyline"
  | "polygon"
  | "line"
  | "rect"
  | "ellipse"
  | "slot"
  | "freehand";

/** Target segment length (mm) when sampling a curved seam outline into a
 *  polyline — small enough that a circle / oblong reads as smooth, coarse
 *  enough to keep the stored point count reasonable. */
/** A drag-out shape's geometry: anchors plus, for the curved kinds, the bezier
 *  handles that make them curves rather than many-sided approximations (#982). */
interface SeamShape {
  points: Vec2[];
  handles?: SeamHandle[];
}

/** How far the pointer must travel before a freehand stroke records another
 *  sample (#997). Matches `ShapeTool`'s freehand buffer: dense enough to trace
 *  a curve faithfully, sparse enough to keep the stored seam sane. */
const FREEHAND_MIN_STEP_MM = 1.0;

const PREVIEW_STROKE = () => cssVar("--piece-cut");
/** Screen px (÷ zoom → mm) within which a stitch-tool press counts as landing
 *  on a part / cutout / hole edge and begins an offset-from-edge drag (#768). */
const OFFSET_EDGE_TOL_PX = 6;
/** Minimum inset (project mm) for an offset seam to commit — below it a press
 *  is a no-op (a click with no drag and no typed Distance). Matches the
 *  construction offset's OFFSET_MIN_DISTANCE_MM. */
const OFFSET_MIN_INSET_MM = 0.5;

/**
 * Click points to draw a polyline stitch seam; double-click or click near the
 * first point to finish. While drawing, a live ghost shows the in-progress
 * polyline plus the chisel marks at the current pitch / size / shape so the
 * user can place a precise number of stitches.
 *
 * Clicking on an existing stitch line drags the whole seam (model commit on
 * release).
 */
export class StitchTool extends ToolBase implements DrawGestureHost {
  id = "stitch";
  label = "Stitch";
  cursor = "crosshair";

  /** Shared click-to-add gesture (#804 IE2). The Stitch tool OWNS the engine
   *  and implements `DrawGestureHost`; only the polyline / polygon kinds drive
   *  it. Offset-from-edge (#768), the drag-out shape kinds (line / rect /
   *  ellipse / slot), and the move-existing-seam drag stay tool-owned. The
   *  leather vertex/angle/grid snap, the working-area clamp, and the live
   *  chisel-mark preview are injected via the host hooks below. */
  private drawEngine = new DrawGestureEngine(this);

  /** Active seam kind. `offset` (default) drags a seam parallel to a part edge
   *  (#768); `polyline` clicks vertices; the shape kinds drag out a closed
   *  outline (#766). */
  kind: StitchKind = "offset";
  /** Freehand (#997) sample buffer: raw pointer positions accumulated during
   *  the stroke, converted to the seam's points on release. Only the `freehand`
   *  kind fills it. */
  private freehandBuffer: Vec2[] = [];
  /** Smoothness seeded onto the next traced seam (#1016). */
  private freehandSmoothness = DEFAULT_LEATHER_SMOOTHNESS;

  /** Tool-level default for the next freehand trace (#1016), set from the tool
   *  settings panel and persisted across sessions. */
  setFreehandSmoothness(v: number) {
    this.freehandSmoothness = v;
  }
  /** Slot (pill) short-axis width in project mm, for the `slot` kind. */
  slotWidth = 6;
  // Shape-drag state (non-polyline kinds): press point → cursor, plus the
  // latched "draw from centre" flag (Alt / centre snap).
  private shapeStart: paper.Point | null = null;
  /** True while the polygon kind is running a regular-polygon drag-out
   *  (#928) — routes the click-to-add kind through the shape-drag flow. */
  private regularPolygonDrag = false;

  /** Shared regular-polygon gesture (#928/#931/#934) — the same state machine
   *  the construction Draw tool runs (auto-n two-stage flow, click-move-click,
   *  pinned-size aim stage, pinned-circle variable count). Ghosts render as
   *  the seam preview (guide + chisel marks); commits go through
   *  `commitPoints`, which resolves the owning part. */
  private polyGesture = new RegularPolygonGesture({
    getScope: () => this.ctx.scene.getPaperScope(),
    getScene: () => this.ctx.scene,
    getViewportView: () => this.ctx.viewport.view,
    getSettings: () => this.ctx.settings,
    renderPreview: (shape) => this.renderPolygonGhost(shape),
    clearPreview: () => this.cleanupPreview(),
    commitVertices: (pts) => {
      this.snapTargets = null; // geometry will change — rebuild next use
      this.commitPoints(
        pts.map((p) => ({ x: p.x, y: p.y })),
        true,
      );
    },
    notifyDraftChange: () => this.drawEngine.notifyDraftChange(),
    liveSides: (sides, radiusMm) => this.polygonLiveCb?.(sides, radiusMm),
    liveSideLength: (mm) => this.polygonLiveSideLenCb?.(mm),
    liveRadius: (mm) => this.polygonLiveRadiusCb?.(mm),
    liveClear: () => this.polygonLiveClearCb?.(),
  });
  /** Live panel mirror for the polygon gesture (#931/#934) — injected by
   *  app.ts, same contract as the construction tool's. */
  private polygonLiveCb: ((sides: number, radiusMm: number) => void) | null =
    null;
  private polygonLiveSideLenCb: ((mm: number) => void) | null = null;
  private polygonLiveRadiusCb: ((mm: number) => void) | null = null;
  private polygonLiveClearCb: (() => void) | null = null;

  /** Re-render an in-flight regular-polygon stage after a panel value edit
   *  (#934) — app.ts calls this on settings changes. */
  refreshPolygonGesture(): void {
    this.polyGesture.refresh();
  }

  setPolygonLiveCallbacks(
    live: (sides: number, radiusMm: number) => void,
    liveSideLength: (mm: number) => void,
    liveRadius: (mm: number) => void,
    clear: () => void,
  ): void {
    this.polygonLiveCb = live;
    this.polygonLiveSideLenCb = liveSideLength;
    this.polygonLiveRadiusCb = liveRadius;
    this.polygonLiveClearCb = clear;
  }

  /** Ghost renderer for the shared polygon gesture: the ring (or the open
   *  first side) previews as a real seam — guide line + the chisel marks it
   *  will carry — via the same renderer the drag-out kinds use. */
  private renderPolygonGhost(shape: PolygonPreviewShape): void {
    this.cleanupPreview();
    if (shape.kind === "line") {
      this.renderSeamGhost([shape.a, shape.b], false);
    } else {
      this.renderSeamGhost(shape.vertices, true);
    }
  }
  private shapeCurrent: paper.Point | null = null;
  private shapeFromCenter = false;
  private lastShapeSample: PointerSample | null = null;
  /** Last hover sample / point — replayed into the staged polygon hover when
   *  a modifier toggles mid-stage (#931). */
  private lastStageHoverSample: PointerSample | null = null;
  private lastStageHoverP: { x: number; y: number } | null = null;
  private lastShapeP: { x: number; y: number } | null = null;

  private previewItem: paper.Item | null = null;
  /** Part the in-progress polyline is anchored to. Set when the first vertex
   *  is placed (whichever part contains it); subsequent vertices and the
   *  hover preview are clamped to this part's outline so the seam never
   *  leaves the leather. */
  private currentPolylinePieceId: string | null = null;

  private dragPieceId: string | null = null;
  private dragStitchId: string | null = null;
  private draggedItem: paper.Item | null = null;
  private dragStart: { x: number; y: number } | null = null;
  private dragLast: { x: number; y: number } | null = null;
  /** Offset-from-edge drag (#768): a press on a part / cutout / hole edge sets
   *  this; dragging lays a seam parallel to that edge, offset by the drag
   *  distance (or the typed Distance preset). `wholeSign` / `segSign` are the
   *  direction multipliers resolved once at press time — into the leather:
   *  inward from a part outline, outward from a cutout / hole. */
  private offsetDrag: {
    pieceId: string;
    sourcePathData: string;
    /** The target part's outline in the SAME (displayed) space the seam points
     *  live in, used to clip the offset seam to the leather (#780). Null when
     *  the part is gone / unresolved (then no clip runs). */
    pieceOutlineData: string | null;
    segment: { pathIndex: number; curveIndex: number };
    startPt: paper.Point;
    wholeSign: number;
    segSign: number;
  } | null = null;
  private lastOffsetSample: PointerSample | null = null;
  private lastOffsetP: { x: number; y: number } | null = null;
  /** Offset Distance preset (#768, mirrors ConstructionTool). When set (non-null,
   *  non-zero) the inset magnitude is fixed at this value so a click on an edge
   *  commits at that distance; null → the drag distance defines it and the live
   *  value is mirrored back into the panel input. */
  private presetOffsetDistance: number | null = null;
  /** Rounded-corner preference for the next offset seam (#423) — the same
   *  choice the construction Offset sub-tool carries, so an inset seam around
   *  a square part can keep square corners instead of arcing them. */
  private offsetRoundedCorners = true;
  /** Panel callback fed the live inset magnitude each frame while dragging with
   *  no Distance preset, so the input reads out the current value. */
  private liveOffsetDistanceCallback?: (mm: number) => void;
  /** Offset "single edge" toggle (mirrors #681). When on, an offset drag follows
   *  just the grabbed edge instead of the whole outline; a held Alt does the
   *  same per-gesture. */
  private singleSegmentOffset = false;
  private unsubscribeSettings: (() => void) | null = null;
  /** Snap targets (existing vertices / hole centres) gathered lazily and
   *  reused until the project changes (#761). */
  private snapTargets: DrawSnapTargets | null = null;

  override onActivate() {
    this.snapTargets = null;
    this.cancelDraft();
    this.resetShapeDraft();
    this.unsubscribeSettings = this.ctx.onSettingsChanged(() => {
      // Rebuild the in-progress polyline / polygon ghost when chisel parameters
      // change (the engine owns the draft preview via `buildPreview`).
      if (this.drawEngine.points.length > 0) this.drawEngine.updatePreview();
    });
  }

  setKind(kind: StitchKind) {
    if (this.kind === kind) return;
    this.kind = kind;
    // Dropping mid-draft is safest — a polyline in progress has no meaning as a
    // rectangle, and vice-versa.
    this.cancelDraft();
    this.resetShapeDraft();
    this.resetOffsetDrag();
    this.cleanupPreview();
  }

  setSlotWidth(width: number) {
    if (width > 0) this.slotWidth = width;
  }

  /** Offset Distance preset — `null` clears it (drag-defined). */
  setOffsetRoundedCorners(on: boolean) {
    this.offsetRoundedCorners = on;
  }

  setPresetOffsetDistance(mm: number | null) {
    this.presetOffsetDistance = mm;
  }

  /** Register the per-frame live-distance callback (panel input mirror). */
  setLiveOffsetDistanceCallback(fn: (mm: number) => void) {
    this.liveOffsetDistanceCallback = fn;
  }

  getSingleSegmentOffset(): boolean {
    return this.singleSegmentOffset;
  }

  setSingleSegmentOffset(on: boolean) {
    this.singleSegmentOffset = on;
  }

  /** Whether placing the next vertex at `pt` closes the polyline draft on its
   *  first vertex — the engine only auto-closes the polygon kind, but a seam
   *  drawn back onto its start is a loop too. */
  private closesPolylineAt(pt: paper.Point): boolean {
    return (
      this.kind === "polyline" &&
      this.drawEngine.points.length >= 2 &&
      this.drawEngine.points[0]!.getDistance(pt) <
        closeSnapRadius(this.ctx.viewport.zoom)
    );
  }

  /** Click-to-add kinds (vertex by vertex): polyline (open), polygon
   *  (closed), and a single line, which is a polyline that ends after one
   *  segment (#1113). The rest (rect / ellipse / slot) are drag-out. */
  private isClickKind(): boolean {
    return (
      this.kind === "polyline" ||
      this.kind === "polygon" ||
      this.kind === "line"
    );
  }

  /** Snap a raw cursor point onto a nearby vertex / the grid and light the
   *  in-draw snap indicator. Targets are cached until the project changes. */
  private snap(p: Point2): DrawSnapResult {
    if (!this.snapTargets) {
      const scope = this.ctx.scene.getPaperScope();
      this.snapTargets = gatherDrawSnapTargets(
        this.ctx.history.state,
        scope,
        this.ctx.scene.getPartLayoutView(),
      );
    }
    const result = resolveDrawSnap(this.ctx, p, this.snapTargets);
    this.ctx.scene.setDrawSnapPoint(result.hit?.pos ?? null);
    return result;
  }

  override onDeactivate() {
    this.cancelDraft();
    this.resetShapeDraft();
    this.resetOffsetDrag();
    this.cleanupPreview();
    this.resetDrag();
    this.cancelMatchPick();
    this.unsubscribeSettings?.();
    this.unsubscribeSettings = null;
    setCanvasCursor(this.ctx.scene.getPaperScope(), "");
    setAngleHint(null);
  }

  override onCancel() {
    if (this.draggedItem) this.ctx.rerender();
    this.resetDrag();
    this.resetOffsetDrag();
    this.resetShapeDraft();
    this.cancelDraft();
    this.cancelMatchPick();
    setAngleHint(null);
  }

  /** Arm the match-partner pick (#199). `source` is the seam that will FOLLOW;
   *  the next seam picked — on the canvas OR in the layers list — becomes the
   *  MAIN it follows. A click elsewhere (or Esc) cancels. */
  armMatchPick(source: StitchRef) {
    this.cancelDraft();
    this.resetDrag();
    stitchMatchPick.arm(source);
    setCanvasCursor(this.ctx.scene.getPaperScope(), "crosshair");
    showCanvasToast(
      "Click the seam this one should follow (on canvas or list)",
    );
  }

  private cancelMatchPick() {
    if (!stitchMatchPick.isArmed()) return;
    stitchMatchPick.cancel();
    setCanvasCursor(this.ctx.scene.getPaperScope(), "crosshair");
  }

  override onDown(s: PointerSample, p: { x: number; y: number }) {
    const scope = this.ctx.scene.getPaperScope();
    const pt = new scope.Point(p.x, p.y);

    // Stitch-match partner pick (#199): the next stitch click completes the
    // match; a miss or a click on the source seam cancels.
    if (stitchMatchPick.isArmed()) {
      const hit = this.ctx.scene.hitTest(pt);
      const created =
        hit.stitchId && hit.pieceId
          ? stitchMatchPick.complete(this.ctx, {
              pieceId: hit.pieceId,
              stitchId: hit.stitchId,
            })
          : (stitchMatchPick.cancel(), false);
      if (!created) showCanvasToast("Match cancelled");
      setCanvasCursor(scope, "crosshair");
      return;
    }

    // Regular-polygon stages (#931/#934): the press commits the aimed /
    // previewed polygon seam, or ends the click-move-click first side. Must
    // run before the seam-drag hit test so the committing click can't grab
    // an existing seam instead.
    if (this.kind === "polygon" && this.polyGesture.press(s, p)) return;

    // Drag an existing stitch line if hit (only when no polyline draft is in
    // progress — otherwise clicks add vertices).
    if (this.drawEngine.points.length === 0) {
      const hit = this.ctx.scene.hitTest(pt);
      if (hit.stitchId && hit.pieceId) {
        // Multi-stitch selection for "Match selected" (#199 path B): Shift =
        // additive, Cmd/Ctrl = toggle. A modifier-click extends the set (which
        // can span pieces) and never starts a drag; a plain click single-selects
        // and grabs the seam to drag.
        if (s.shiftKey || s.ctrlKey || s.metaKey) {
          const toggle = s.ctrlKey || s.metaKey;
          const currentIds = this.ctx.scene.getSelection().stitchIds ?? [];
          const inSet = currentIds.includes(hit.stitchId);
          const nextIds = toggle
            ? inSet
              ? currentIds.filter((id) => id !== hit.stitchId)
              : [...currentIds, hit.stitchId]
            : inSet
              ? currentIds
              : [...currentIds, hit.stitchId];
          const leadId = nextIds[nextIds.length - 1] ?? null;
          const leadPieceId = leadId
            ? (pieceForStitchLine(this.ctx.history.state, leadId)?.id ??
              hit.pieceId)
            : null;
          this.ctx.setSelection({
            pieceId: leadPieceId,
            decorationId: null,
            stitchId: leadId,
            stitchIds: nextIds,
          });
          return;
        }
        this.dragPieceId = hit.pieceId;
        this.dragStitchId = hit.stitchId;
        this.dragStart = { x: p.x, y: p.y };
        this.dragLast = { x: p.x, y: p.y };
        this.ctx.setSelection({
          pieceId: hit.pieceId,
          decorationId: null,
          stitchId: hit.stitchId,
        });
        this.draggedItem = this.ctx.scene.getStitchLineItem(
          hit.pieceId,
          hit.stitchId,
        );
        return;
      }
    }

    // Offset seam kind (#768): a press on/near a part / cutout / hole edge
    // begins an offset drag — the seam parallels that edge, offset into the
    // leather (inward from a part outline, outward from a cutout / hole),
    // sized by the drag distance or the typed Distance. Its own seam kind, so
    // it never collides with the shape-sweep / vertex gestures; a press off any
    // edge is a no-op.
    if (this.kind === "offset") {
      const edge = this.findEdgeUnderCursor(scope, pt);
      if (edge) {
        this.offsetDrag = {
          pieceId: edge.pieceId,
          sourcePathData: edge.sourcePathData,
          pieceOutlineData: this.displayedPieceOutline(scope, edge.pieceId),
          segment: edge.segment,
          startPt: pt,
          wholeSign: offsetSign(
            edge.sourcePathData,
            scope,
            null,
            edge.wantInside,
          ),
          segSign: offsetSign(
            edge.sourcePathData,
            scope,
            edge.segment,
            edge.wantInside,
          ),
        };
        this.lastOffsetSample = s;
        this.lastOffsetP = p;
        // The drag owns the live preview + ray now — drop the hover arrow.
        this.ctx.scene.setOffsetHoverAffordance(null);
        // Seed the preview immediately so a typed Distance shows (and a click
        // commits) even without any drag movement.
        this.updateOffsetDrag(s, p);
      }
      return;
    }

    // Regular-polygon drag-out (#928): with the Equilateral toggle or the
    // size / centre snap active, the polygon kind drags out a regular n-gon
    // seam instead of click-to-add (never mid-draft).
    if (
      this.kind === "polygon" &&
      this.drawEngine.points.length === 0 &&
      (this.ctx.settings.polygonEquilateral ||
        isSizesSnapActive(this.ctx.settings, s.shiftKey) ||
        isCenterSnapActive(this.ctx.settings, s.altKey))
    ) {
      this.regularPolygonDrag = true;
    }
    // Drag-out kinds (#766): a press begins a drag that sweeps out a straight
    // line / rectangle / ellipse / slot; its outline becomes the seam on
    // release. Click-to-add kinds (polyline / polygon) fall through.
    if (!this.isClickKind() || this.regularPolygonDrag) {
      const start = this.snap(p).point;
      const sp = new scope.Point(start.x, start.y);
      this.shapeStart = sp;
      this.shapeCurrent = sp;
      this.shapeFromCenter = isCenterSnapActive(this.ctx.settings, s.altKey);
      this.lastShapeSample = s;
      // Freehand traces the pointer rather than sweeping a primitive out of
      // two corners, so it collects its own samples (#997).
      if (this.kind === "freehand") this.freehandBuffer = [{ ...start }];
      return;
    }

    // Click-to-add kinds (polyline / polygon) run through the shared engine
    // (#804 IE2). Two stitch-specific gates wrap the delegation:
    //  1. the FIRST vertex must land inside a part — that anchors the seam to
    //     the part it'll belong to and supplies the outline every later vertex
    //     (and the hover preview) is clamped against, so the seam never leaves
    //     the leather. Clicks outside every part are silently ignored.
    //  2. a click near the first vertex CLOSES a polyline too, not just a
    //     polygon (the engine only auto-closes the polygon kind) — with ≥ 3
    //     vertices it commits a real loop, with exactly 2 it closes open.
    // The snap tiers + working-area clamp are injected via `resolveDraftPlacement`.
    if (this.drawEngine.points.length === 0) {
      const anchor = this.findPieceContaining(scope, pt);
      if (!anchor) return;
      this.currentPolylinePieceId = anchor.pieceId;
    } else if (hasHover(s.pointerType) && this.closesPolylineAt(pt)) {
      this.commitDraft(this.drawEngine.points.length >= 3);
      return;
    }
    this.drawEngine.onDown(s, p);
  }

  override onUp(s: PointerSample, p: { x: number; y: number }) {
    // Offset seam kind (#768): commit whenever the resolved inset is large
    // enough — a drag past the minimum, or a click when a Distance is typed.
    // Too small (a bare click with no preset) is a no-op.
    if (this.offsetDrag) {
      const od = this.offsetDrag;
      this.offsetDrag = null;
      this.lastOffsetSample = null;
      this.lastOffsetP = null;
      const scope = this.ctx.scene.getPaperScope();
      this.cleanupPreview();
      this.ctx.scene.clearGuideRays();
      this.ctx.scene.setDrawSnapPoint(null);
      const built = this.buildOffsetSeam(scope, od, s.altKey, p);
      if (built && built.points.length >= (built.closed ? 3 : 2)) {
        this.snapTargets = null; // geometry will change — rebuild next use
        // Pin the seam to the part the offset started on (#768) — never let an
        // overshooting offset re-home onto a part lying beneath it.
        this.commitPoints(built.points, built.closed, od.pieceId);
      }
      return;
    }
    // Drag-out kinds (#766): commit the swept outline — rect / ellipse / slot
    // are closed rings.
    if (
      (!this.isClickKind() || this.regularPolygonDrag) &&
      this.shapeStart &&
      this.shapeCurrent
    ) {
      const scope = this.ctx.scene.getPaperScope();
      if (this.kind === "polygon" && this.regularPolygonDrag) {
        // Regular-polygon release (#928/#931/#934): the shared gesture
        // commits the fixed-count seam ring or transitions into a stage
        // (auto-n stage 2 / click-move-click anchor / pinned-size aim).
        const a = { x: this.shapeStart.x, y: this.shapeStart.y };
        const b = { x: this.shapeCurrent.x, y: this.shapeCurrent.y };
        const fromCenter = this.shapeFromCenter;
        this.resetShapeDraft();
        this.cleanupPreview();
        setAngleHint(null);
        this.ctx.scene.setDrawSnapPoint(null);
        this.polyGesture.release(a, b, fromCenter);
        return;
      }
      if (this.kind === "freehand") {
        // A traced seam is a stroke, not a ring: it commits OPEN, like the
        // polyline kind, so the row of stitches follows the path drawn rather
        // than closing back to the start (#997).
        const traced = this.freehandPoints(scope);
        const trace = this.freehandBuffer.map((q) => ({ x: q.x, y: q.y }));
        this.resetShapeDraft();
        setAngleHint(null);
        this.cleanupPreview();
        if (traced && traced.points.length >= 2) {
          this.snapTargets = null;
          this.commitPoints(traced.points, false, undefined, traced.handles, {
            rawPoints: trace,
            smoothness: this.freehandSmoothness,
          });
        } else {
          this.ctx.scene.setDrawSnapPoint(null);
        }
        return;
      }
      const ring = this.buildShapeRing(
        scope,
        this.shapeStart,
        this.shapeCurrent,
      );
      this.resetShapeDraft();
      setAngleHint(null);
      if (ring && ring.points.length >= 3) {
        // Reuse the shared commit path — it picks the owning part and commits
        // the closed StitchLine. Clear the swept ghost first (the commit
        // doesn't touch this tool preview).
        this.cleanupPreview();
        this.snapTargets = null; // geometry will change — rebuild next use
        this.commitPoints(ring.points, true, undefined, ring.handles);
      } else {
        this.cleanupPreview();
        this.ctx.scene.setDrawSnapPoint(null);
      }
      return;
    }
    // A pointer with no hover places its vertex on RELEASE (#1116) — and it
    // is the release, not the press, that closes a polyline on its start.
    if (this.isClickKind() && !hasHover(s.pointerType)) {
      const scope = this.ctx.scene.getPaperScope();
      if (
        this.drawEngine.points.length > 0 &&
        this.closesPolylineAt(new scope.Point(p.x, p.y))
      ) {
        this.commitDraft(this.drawEngine.points.length >= 3);
        return;
      }
      this.drawEngine.onUp(s, p);
    }
    // A single Line ends the moment its segment exists (#1113).
    if (this.drawEngine.maybeFinishSingleSegment()) return;
    if (this.dragStart && this.dragStitchId && this.dragPieceId) {
      const dx = p.x - this.dragStart.x;
      const dy = p.y - this.dragStart.y;
      const pieceId = this.dragPieceId;
      const stitchId = this.dragStitchId;
      this.resetDrag();
      if (Math.abs(dx) >= 0.001 || Math.abs(dy) >= 0.001) {
        this.snapTargets = null; // geometry changed — rebuild targets next use
        const moveScope = this.ctx.scene.getPaperScope();
        this.ctx.commit("Move stitch line", (proj) =>
          translateStitchLineInProject(
            proj,
            pieceId,
            stitchId,
            dx,
            dy,
            moveScope,
          ),
        );
      }
    }
  }

  override onMove(s: PointerSample, p: { x: number; y: number }) {
    // Offset-from-edge (#768): update the parallel seam preview while dragging.
    if (this.offsetDrag) {
      this.updateOffsetDrag(s, p);
      return;
    }
    // Drag-out kinds (#766): sweep out the line / shape outline while dragging.
    if ((!this.isClickKind() || this.regularPolygonDrag) && this.shapeStart) {
      this.updateShapeDrag(s, p);
      return;
    }
    if (this.dragLast && this.draggedItem) {
      // Translate the seam with the cursor every frame — same as the cutout /
      // hole live drags. (A previous "keep every vertex inside the part" guard
      // froze the drag near an edge yet onUp committed the move anyway, so it
      // only broke the live feedback without enforcing anything — #766 fix.)
      const dx = p.x - this.dragLast.x;
      const dy = p.y - this.dragLast.y;
      if (dx !== 0 || dy !== 0) {
        const scope = this.ctx.scene.getPaperScope();
        this.draggedItem.translate(new scope.Point(dx, dy));
        this.dragLast = { x: p.x, y: p.y };
      }
      return;
    }
    // Click-kind draft: a press-drag carries no bezier shaping for stitch, so
    // this is a no-op — but keep the engine's flow consistent with the other
    // tools (#804 IE2).
    if (this.isClickKind() && this.drawEngine.points.length > 0) {
      this.drawEngine.onMove(s, p);
    }
  }

  override onHover(s: PointerSample, p: { x: number; y: number }) {
    // Regular-polygon stages (#931/#934): the hover rotates the aimed
    // polygon, rubber-bands the click-move-click side, or drives the auto-n
    // stage-2 preview. Capture the sample/point so `onModifiersChanged` can
    // replay it when Ctrl/Cmd toggles mid-stage.
    this.lastStageHoverP = p;
    this.lastStageHoverSample = s;
    if (this.kind === "polygon" && this.polyGesture.hover(s, p)) return;
    // Click-kind draft in progress → the shared engine drives the rubber-band
    // hover, the snap tiers + working-area clamp (via `resolveDraftHover`), the
    // segment ray, and the live chisel preview (#804 IE2). Stitch does NOT
    // suppress the pending-edge ray while hovering to close (unlike Shape).
    if (this.isClickKind() && this.drawEngine.points.length > 0) {
      this.drawEngine.onHover(s, p);
      return;
    }
    // Otherwise (no draft, or an offset / shape / move kind): pre-press feedback.
    this.prePressHover(this.ctx.scene.getPaperScope(), p);
  }

  /** Pre-press hover feedback with no active click-draft: over an existing seam
   *  the cursor becomes "move" (a press starts a seam drag) and the snap
   *  indicator is cleared; in the Offset seam kind a plain pointer + the
   *  perpendicular-arrow affordance light over an offsettable edge (#148 / #768);
   *  elsewhere the first-vertex / start snap ring lights. No segment is in
   *  progress yet, so any guide ray is cleared. */
  private prePressHover(scope: paper.PaperScope, p: { x: number; y: number }) {
    const pt = new scope.Point(p.x, p.y);
    const hit = this.ctx.scene.hitTest(pt);
    const overStitch = !!hit.stitchId;
    if (overStitch) {
      setCanvasCursor(scope, "move");
      this.ctx.scene.setDrawSnapPoint(null);
      this.ctx.scene.setOffsetHoverAffordance(null);
      this.ctx.scene.clearGuideRays();
      return;
    }
    if (this.kind === "offset") {
      // Mirror the construction Offset tool (#148): a plain pointer (the
      // gesture grabs an edge, it doesn't place a point), plus a small arrow
      // next to the cursor when it's over an offsettable part / cutout / hole
      // edge, pointing the way the seam will inset with a tick for the seam.
      setCanvasCursor(scope, "default");
      this.ctx.scene.setDrawSnapPoint(null);
      this.ctx.scene.setOffsetHoverAffordance(
        this.computeOffsetHoverSpec(scope, pt),
      );
      this.ctx.scene.clearGuideRays();
      return;
    }
    setCanvasCursor(scope, "crosshair");
    this.ctx.scene.setOffsetHoverAffordance(null);
    this.snap(p);
    this.ctx.scene.clearGuideRays();
  }

  /** Geometry for the Offset seam kind's hover affordance (#148 / #768):
   *  the nearest offsettable edge under the cursor plus resolved unit
   *  vectors — `dir` the way the seam will inset (into a part outline,
   *  out of a cutout / hole) and `tan` parallel to the edge (the seam
   *  tick). Null when the cursor isn't over an offsettable edge. */
  private computeOffsetHoverSpec(
    scope: paper.PaperScope,
    pt: paper.Point,
  ): {
    x: number;
    y: number;
    dirX: number;
    dirY: number;
    tanX: number;
    tanY: number;
  } | null {
    const edge = this.findEdgeUnderCursor(scope, pt);
    if (!edge) return null;
    const geo = offsetHoverGeometry(
      edge.sourcePathData,
      edge.segment,
      { x: pt.x, y: pt.y },
      scope,
      edge.wantInside,
    );
    if (!geo) return null;
    return { x: pt.x, y: pt.y, ...geo };
  }

  /** Double-click finishes a click-to-add seam — closed for the `polygon`
   *  kind, open for `polyline` (mirrors the Cutout polygon behaviour).
   *  Delegates to the engine (#804 IE2). */
  handleDoubleClick(): boolean {
    return this.drawEngine.finishOnDoubleClick();
  }

  /** Discard an in-progress polyline / polygon draft regardless of how many
   *  vertices were placed. Returns true when there was a draft to drop. Wired
   *  to the red "X" in the touch fly-out so users without a keyboard can bail. */
  handleDiscard(): boolean {
    // Regular-polygon stages (#931/#934): Escape abandons the staged draft.
    if (this.kind === "polygon" && this.polyGesture.escape()) return true;
    if (this.drawEngine.points.length === 0) return false;
    this.cancelDraft();
    return true;
  }

  /** Number of vertices currently placed in the in-progress draft. 0 means
   *  there's no draft. Lets the surrounding UI sync flyout buttons on
   *  activation without observing every internal mutation. */
  get vertexCount(): number {
    // Regular-polygon stages (#931/#934) surface as 2 (✓ enabled — Enter
    // commits the preview) / 1 (✓ disabled, ✗ discards).
    if (this.kind === "polygon") {
      const staged = this.polyGesture.vertexCount();
      if (staged > 0) return staged;
    }
    return this.drawEngine.vertexCount;
  }

  /** Subscribe to draft-state changes — fired whenever the in-progress
   *  vertex count goes up or down. Pass `null` to unsubscribe. Delegates to
   *  the engine, which owns the draft state (#804 IE2). */
  setDraftChangeListener(fn: ((vertexCount: number) => void) | null) {
    this.drawEngine.setDraftChangeListener(fn);
  }

  /** End the seam at the last clicked vertex — useful when the user wants
   *  to stop without an explicit double-click. Wired to both Escape and
   *  Enter from app.ts. Delegates to the engine: a ≥ 2-vertex draft commits
   *  (closed only for a ≥ 3-vertex polygon), a shorter draft is discarded. */
  handleFinish(): boolean {
    // Regular-polygon stages (#931/#934): Enter commits the staged preview.
    if (this.kind === "polygon" && this.polyGesture.finish()) return true;
    return this.drawEngine.handleFinish();
  }

  private shouldSnap(s: PointerSample): boolean {
    // Stitch lines are polyline-only — the only snap behaviour
    // applicable is the angle snap. Sizes / point snap don't apply.
    return isAngleSnapActive(this.ctx.settings, angleModifierHeld(s));
  }

  private resetDrag() {
    this.dragPieceId = null;
    this.dragStitchId = null;
    this.draggedItem = null;
    this.dragStart = null;
    this.dragLast = null;
  }

  /** DrawGestureHost: full draft reset — clear the engine draft, this tool's
   *  own preview, the guide rays / angle hint / snap indicator, and the
   *  anchored part. The engine's `notifyDraftChange` hides the fly-out. */
  cancelDraft() {
    this.polyGesture.cancel();
    this.drawEngine.reset();
    this.currentPolylinePieceId = null;
    this.cleanupPreview();
    this.ctx.scene.clearGuideRays();
    setAngleHint(null);
    this.ctx.scene.setDrawSnapPoint(null);
    this.drawEngine.notifyDraftChange();
  }

  /** The part a new seam should anchor to at `pt` — used when placing the
   *  polyline's first vertex. Never a hidden part, prefers the selected part,
   *  else the top-most visible part under the point (#783). Null if the click
   *  isn't inside any visible part. */
  private findPieceContaining(scope: paper.PaperScope, pt: paper.Point) {
    return resolveDrawTarget(
      this.ctx.history.state,
      { x: pt.x, y: pt.y },
      scope,
      this.ctx.scene.getSelection().pieceId,
      this.ctx.scene.getPartLayoutView(),
    );
  }

  /** Clamp `pt` so it lies inside the polyline's active part. Points already
   *  inside pass through unchanged; points outside are pulled to the nearest
   *  point on the part's outline so the seam never escapes the leather. */
  private clampToActivePiece(
    scope: paper.PaperScope,
    pt: paper.Point,
  ): paper.Point {
    if (!this.currentPolylinePieceId) return pt;
    const project = this.ctx.history.state;
    const piece = project.pieces.find(
      (p) => p.id === this.currentPolylinePieceId,
    );
    if (!piece) return pt;
    let path: paper.PathItem;
    try {
      path = new scope.CompoundPath({
        pathData: piece.shapePathData,
        insert: false,
      });
    } catch {
      return pt;
    }
    // `pt` is in displayed space; in Spread view a moved linked part's outline
    // lives `offset` away in model space, so pull it into displayed space
    // before clamping — otherwise every vertex would be clamped to the part's
    // ORIGINAL (empty) location (#786).
    const offset = pieceDisplayOffset(
      piece,
      this.ctx.scene.getPartLayoutView(),
    );
    if (offset.dx !== 0 || offset.dy !== 0) {
      path.translate(new scope.Point(offset.dx, offset.dy));
    }
    let result = pt;
    if (!path.contains(pt)) {
      const nearest = path.getNearestPoint(pt);
      if (nearest) result = nearest;
    }
    path.remove();
    return result;
  }

  private cleanupPreview() {
    if (this.previewItem) {
      this.previewItem.remove();
      this.previewItem = null;
    }
  }

  private buildPreviewPoints(): Vec2[] {
    const poly = this.drawEngine.points;
    const hover = this.drawEngine.hoverPt;
    const pts: Vec2[] = poly.map((p) => ({ x: p.x, y: p.y }));
    if (hover) {
      const last = poly[poly.length - 1];
      // Append the hover point only if it's distinct from the last placed
      // vertex — avoids a zero-length segment at the tail.
      if (!last || last.getDistance(hover) > 0.5) {
        pts.push({ x: hover.x, y: hover.y });
      }
    }
    return pts;
  }

  /** True when the cursor is hovering inside the close-snap zone of the first
   *  vertex with enough vertices placed to form a loop. Kind-independent — a
   *  polyline previews closed here too, matching its click-to-close commit.
   *  The live preview switches to a closed-loop visualisation in this state so
   *  the user gets feedback before committing. */
  private isHoveringToClose(): boolean {
    const poly = this.drawEngine.points;
    const hover = this.drawEngine.hoverPt;
    if (poly.length < 3 || !hover) return false;
    const first = poly[0]!;
    return first.getDistance(hover) < closeSnapRadius(this.ctx.viewport.zoom);
  }

  /** DrawGestureHost: build the in-progress seam ghost into the engine-owned
   *  preview `group` — the hairline guide + the live chisel marks
   *  (`stitchHolePlacements`) at the pitch / size / shape the committed seam
   *  will inherit, plus the vertex dots and the close-target halo (#804 IE2).
   *  Recomputes its own `closing` state (kind-independent) rather than trusting
   *  the engine's polygon-only flag, so a polyline previews closed too. */
  buildPreview(group: paper.Group) {
    const scope = this.ctx.scene.getPaperScope();
    const poly = this.drawEngine.points;
    const closing = this.isHoveringToClose();
    // When the hover is in the close-snap zone, drop the trailing hover
    // segment and instead render the polyline as a closed loop so the user
    // sees the seam they're about to commit.
    const pts: Vec2[] = closing
      ? poly.map((p) => ({ x: p.x, y: p.y }))
      : this.buildPreviewPoints();

    // Resolve the chisel parameters the new line will inherit on commit
    // so the live preview matches what the user is about to draw. Falls
    // back to the project block when the polyline isn't anchored to a
    // part yet (shouldn't happen — first vertex anchors it — but guard
    // anyway).
    const project = this.ctx.history.state;
    const piece = this.currentPolylinePieceId
      ? project.pieces.find((p) => p.id === this.currentPolylinePieceId)
      : undefined;
    const eff = piece
      ? resolvePartStitchingDefaults(project, piece)
      : resolveProjectStitchingDefaults(project);
    const previewPitch = effectivePitchFromDefaults(eff);

    if (pts.length >= 2) {
      const guide = new scope.Path({
        segments: pts.map((p) => new scope.Point(p.x, p.y)),
      });
      if (closing) guide.closed = true;
      // Solid hairline matches what the committed seam will look like, so
      // the user can preview spacing without a dashed/solid swap on commit.
      guide.strokeColor = new scope.Color(PREVIEW_STROKE());
      guide.strokeWidth = 0.6;
      guide.strokeScaling = false;
      guide.fillColor = null;
      group.addChild(guide);

      // Preview through the real placement path so the working-area inset (a
      // margin from each end on an open seam; continuous on a closing loop)
      // shows before commit.
      const previewLine: StitchLine = {
        id: "__preview__",
        points: pts,
        pitch: eff.pitch,
        size: eff.size,
        shape: eff.shape,
        slantAngle: eff.slantAngle,
        pitchMode: eff.pitchMode,
        closed: closing,
      };
      const placements = stitchHolePlacements(previewLine);
      for (const place of placements) {
        const data = stitchHolePathData(
          place,
          eff.shape,
          eff.size,
          eff.slantAngle,
          previewPitch,
        );
        try {
          const cp = new scope.CompoundPath({ pathData: data, insert: false });
          cp.strokeColor = new scope.Color(PREVIEW_STROKE());
          cp.strokeWidth = 0.8;
          cp.strokeScaling = false;
          cp.fillColor = new scope.Color(0, 0, 0, 0.25);
          group.addChild(cp);
        } catch {
          /* ignore */
        }
      }
    }

    // Vertex markers so the user sees where they've clicked. The first vertex
    // grows + gets a halo while the cursor is inside its close-snap zone, so
    // the close target is visible before tapping (important on iPad where
    // there's no keyboard shortcut alternative).
    for (let i = 0; i < poly.length; i++) {
      const v = poly[i]!;
      const isCloseTarget = closing && i === 0;
      if (isCloseTarget) {
        const halo = new scope.Path.Circle(v, CLOSE_SNAP_RADIUS);
        halo.strokeColor = new scope.Color(PREVIEW_STROKE());
        halo.strokeColor.alpha = 0.6;
        halo.strokeWidth = 0.6;
        halo.strokeScaling = false;
        halo.fillColor = null;
        group.addChild(halo);
      }
      const dot = new scope.Path.Circle(v, isCloseTarget ? 1.6 : 0.8);
      dot.fillColor = new scope.Color(PREVIEW_STROKE());
      dot.strokeWidth = 0;
      group.addChild(dot);
    }
  }

  /** Update the in-flight shape drag: resolve the cursor (vertex/grid snap,
   *  square/circle or angle constraint, draw-from-centre) and rebuild the
   *  closed-outline preview. */
  private updateShapeDrag(s: PointerSample, p: { x: number; y: number }) {
    if (!this.shapeStart) return;
    const scope = this.ctx.scene.getPaperScope();
    const settings = this.ctx.settings;
    this.shapeFromCenter = isCenterSnapActive(settings, s.altKey);
    this.lastShapeSample = s;
    this.lastShapeP = p;

    if (this.kind === "freehand") {
      // Freehand doesn't snap intermediate samples — it would fight the
      // smoothing — and it has no corner-to-corner constraints to apply. Only
      // the start point snapped, in onDown (#997, matching ShapeTool).
      this.ctx.scene.setDrawSnapPoint(null);
      const last = this.freehandBuffer[this.freehandBuffer.length - 1];
      if (
        !last ||
        Math.hypot(p.x - last.x, p.y - last.y) > FREEHAND_MIN_STEP_MM
      ) {
        this.freehandBuffer.push({ x: p.x, y: p.y });
      }
      this.shapeCurrent = new scope.Point(p.x, p.y);
      this.renderFreehandGhost();
      return;
    }
    let end: { x: number; y: number } = p;
    const vsnap = this.snap(p);
    if (vsnap.hit?.kind === "vertex") {
      // A nearby existing vertex wins over the shape constraints.
      end = vsnap.point;
      setAngleHint(null);
    } else if (
      (this.kind === "slot" &&
        isAngleSnapActive(settings, angleModifierHeld(s))) ||
      // Shift means "regular" for the polygon drag — only Ctrl/Cmd (or the
      // Angle toggle) locks the drag's angle (#931).
      (this.regularPolygonDrag &&
        isAngleSnapActive(settings, s.ctrlKey || s.metaKey))
    ) {
      const sn = snapToCommonAngle(this.shapeStart, p);
      end = { x: sn.x, y: sn.y };
      setAngleHint(sn.angleRad);
    } else if (
      (this.kind === "rect" || this.kind === "ellipse") &&
      isSizesSnapActive(settings, s.shiftKey)
    ) {
      end = constrainSquare(this.shapeStart, p);
      setAngleHint(null);
    } else {
      if (vsnap.hit?.kind === "line" || vsnap.hit?.kind === "grid")
        end = vsnap.point;
      setAngleHint(null);
    }
    this.shapeCurrent = new scope.Point(end.x, end.y);
    if (this.kind === "polygon" && this.regularPolygonDrag) {
      // Regular-polygon drag feedback (#928/#931/#934): the shared gesture
      // renders the seam ghost (via `renderPolygonGhost`), the first-side
      // angle ray, and the live panel mirrors.
      this.polyGesture.dragFeedback(
        s,
        { x: this.shapeStart.x, y: this.shapeStart.y },
        { x: end.x, y: end.y },
        this.shapeFromCenter,
      );
      return;
    }
    // Segment ray (#788): the `slot` (pill) drag-out sweeps a single axis —
    // show its length + angle. The area shapes (rect / ellipse) have no single
    // segment, so no ray.
    if (this.kind === "slot") {
      const { a, b } = resolveDragAxis(
        { x: this.shapeStart.x, y: this.shapeStart.y },
        { x: end.x, y: end.y },
        this.shapeFromCenter,
      );
      emitSegmentRay(this.ctx.scene, a, b);
    } else {
      this.ctx.scene.setGuideRays(null);
    }
    this.updateShapePreview();
  }

  /** Build the seam outline (project-mm polyline) for the current drag-out kind
   *  between two drag points, or null when the drag is too small. A `line`
   *  returns its two endpoints (open); the shapes return a closed ring. */
  private buildShapeRing(
    scope: paper.PaperScope,
    start: paper.Point,
    current: paper.Point,
  ): SeamShape | null {
    if (this.kind === "rect") {
      const box = resolveDragBox(
        { x: start.x, y: start.y },
        { x: current.x, y: current.y },
        { fromCenter: this.shapeFromCenter },
      );
      if (box.width <= 1 || box.height <= 1) return null;
      const right = box.left + box.width;
      const bottom = box.top + box.height;
      return {
        points: [
          { x: box.left, y: box.top },
          { x: right, y: box.top },
          { x: right, y: bottom },
          { x: box.left, y: bottom },
        ],
      };
    }
    if (this.kind === "ellipse") {
      const box = resolveDragBox(
        { x: start.x, y: start.y },
        { x: current.x, y: current.y },
        { fromCenter: this.shapeFromCenter },
      );
      if (box.width <= 1 || box.height <= 1) return null;
      const ell = new scope.Path.Ellipse(
        new scope.Rectangle(
          new scope.Point(box.left, box.top),
          new scope.Size(box.width, box.height),
        ),
      );
      const shape = this.shapeFromPath(scope, ell);
      ell.remove();
      return shape;
    }
    // slot (pill)
    const { a, b } = resolveDragAxis(
      { x: start.x, y: start.y },
      { x: current.x, y: current.y },
      this.shapeFromCenter,
    );
    const data = makeSlotPathData(a, b, this.slotWidth);
    if (!data) return null;
    let cp: paper.CompoundPath;
    try {
      cp = new scope.CompoundPath({ pathData: data, insert: false });
    } catch {
      return null;
    }
    const path = (
      cp.children && cp.children.length ? cp.children[0] : cp
    ) as paper.Path;
    const shape = this.shapeFromPath(scope, path);
    cp.remove();
    return shape;
  }

  /**
   * A drag-out shape's REAL geometry — anchors plus bezier handles.
   *
   * Ellipse and slot used to be flattened here into one point every 2 mm, which was a workaround for seams having no way to
   * store a curve: a circular seam came out as ~60 sampled points, so editing
   * its points showed a crowd of anchors instead of the four an ellipse has, and
   * nudging any one of them dented the curve. Seams carry curves since #982, so
   * the shape is stored as drawn — four anchors for an ellipse, four for a pill.
   */
  private shapeFromPath(
    scope: paper.PaperScope,
    path: paper.Path,
  ): SeamShape | null {
    const seam = seamFromPathData(path.pathData, scope);
    if (!seam || seam.points.length < 3) return null;
    return { points: seam.points, handles: seam.handles };
  }

  /** The part a shape-seam ring would belong to (first point inside, else bbox
   *  overlap) — used only to resolve chisel defaults for the live preview. */
  private previewOwnerPiece(scope: paper.PaperScope, ring: Vec2[]) {
    const project = this.ctx.history.state;
    const first = new scope.Point(ring[0]!.x, ring[0]!.y);
    let overlap: (typeof project.pieces)[number] | undefined;
    for (const piece of project.pieces) {
      let path: paper.PathItem;
      try {
        path = new scope.CompoundPath({
          pathData: piece.shapePathData,
          insert: false,
        });
      } catch {
        continue;
      }
      const inside = path.contains(first);
      const bboxHit =
        !overlap &&
        ring.some((p) => path.bounds.contains(new scope.Point(p.x, p.y)));
      path.remove();
      if (inside) return piece;
      if (bboxHit) overlap = piece;
    }
    return overlap;
  }

  /** Live preview for the drag-out kinds: the outline plus the chisel marks it
   *  will carry, mirroring the polyline preview. Open for `line`, closed for
   *  the shapes. */
  private updateShapePreview() {
    this.cleanupPreview();
    if (!this.shapeStart || !this.shapeCurrent) return;
    const scope = this.ctx.scene.getPaperScope();
    const ring = this.buildShapeRing(scope, this.shapeStart, this.shapeCurrent);
    if (!ring || ring.points.length < 3) return;
    this.renderSeamGhost(ring.points, true, ring.handles);
  }

  /** Live ghost for a freehand stroke: the same seam ghost the drag-out kinds
   *  show (guide line + real chisel marks), drawn OPEN over the samples
   *  collected so far (#997). */
  private renderFreehandGhost(): void {
    this.cleanupPreview();
    if (this.freehandBuffer.length < 2) return;
    this.renderSeamGhost(this.freehandBuffer, false);
  }

  /**
   * The freehand stroke as the seam's geometry (#1016).
   *
   * This used to claim it smoothed the stroke "through the shared
   * `pathFromBuffer`" — it didn't; that helper just joins samples with `L`
   * commands. The stroke was then re-sampled at a fixed 2 mm, so a traced
   * seam came out as a dense polyline no smoother than the hand that drew it.
   *
   * It now simplifies at the tool's smoothness, which since #982 yields a CURVE
   * — few anchors plus handles — that lays holes along the curve and is
   * editable like any other. `rawPoints` keeps the trace so the slider can
   * re-derive it later.
   */
  private freehandPoints(
    scope: paper.PaperScope,
  ): { points: Vec2[]; handles?: SeamHandle[] } | null {
    if (this.freehandBuffer.length < 2) return null;
    return smoothSeamFromRawPoints(
      this.freehandBuffer.map((p) => ({ x: p.x, y: p.y })),
      this.freehandSmoothness,
      false,
      scope,
    );
  }

  /** Render a seam ghost for an arbitrary ring / open run: the guide line
   *  plus the chisel marks it will carry. Shared by the drag-out preview and
   *  the regular-polygon gesture ghost. */
  private renderSeamGhost(
    ring: Vec2[],
    closed: boolean,
    handles?: SeamHandle[],
  ): void {
    const scope = this.ctx.scene.getPaperScope();
    const project = this.ctx.history.state;
    const piece = this.previewOwnerPiece(scope, ring);
    const eff = piece
      ? resolvePartStitchingDefaults(project, piece)
      : resolveProjectStitchingDefaults(project);
    const previewPitch = effectivePitchFromDefaults(eff);

    const group = new scope.Group();
    group.data = { ephemeral: true };

    // Built from the seam's real geometry so a curved ghost previews as the
    // curve it will commit as (#982), not as the chords between its anchors —
    // which for a four-anchor ellipse would preview a diamond.
    const guide = new scope.Path({
      pathData: seamPathData({ points: ring, handles, closed }),
    });
    guide.closed = closed;
    guide.strokeColor = new scope.Color(PREVIEW_STROKE());
    guide.strokeWidth = 0.6;
    guide.strokeScaling = false;
    guide.fillColor = null;
    group.addChild(guide);

    const previewLine: StitchLine = {
      id: "__preview__",
      points: ring,
      handles,
      pitch: eff.pitch,
      size: eff.size,
      shape: eff.shape,
      slantAngle: eff.slantAngle,
      pitchMode: eff.pitchMode,
      closed,
    };
    for (const place of stitchHolePlacements(previewLine)) {
      const data = stitchHolePathData(
        place,
        eff.shape,
        eff.size,
        eff.slantAngle,
        previewPitch,
      );
      try {
        const cp = new scope.CompoundPath({ pathData: data, insert: false });
        cp.strokeColor = new scope.Color(PREVIEW_STROKE());
        cp.strokeWidth = 0.8;
        cp.strokeScaling = false;
        cp.fillColor = new scope.Color(0, 0, 0, 0.25);
        group.addChild(cp);
      } catch {
        /* ignore */
      }
    }
    this.previewItem = group;
  }

  private resetShapeDraft() {
    this.regularPolygonDrag = false;
    this.freehandBuffer = [];
    this.shapeStart = null;
    this.shapeCurrent = null;
    this.shapeFromCenter = false;
    this.lastShapeSample = null;
    this.lastShapeP = null;
    this.ctx.scene.clearGuideRays();
  }

  private resetOffsetDrag() {
    this.offsetDrag = null;
    this.lastOffsetSample = null;
    this.lastOffsetP = null;
    this.ctx.scene.clearGuideRays();
    this.ctx.scene.setOffsetHoverAffordance(null);
  }

  /** The nearest offsettable edge under `pt` (within the edge tolerance),
   *  across every visible part outline, cutout, and hole — or null. Each source
   *  carries `wantInside`: a part outline offsets INTO the part (inward), while
   *  a cutout / hole offsets OUT into the surrounding leather (#768). */
  private findEdgeUnderCursor(
    scope: paper.PaperScope,
    pt: paper.Point,
  ): {
    pieceId: string;
    sourcePathData: string;
    segment: { pathIndex: number; curveIndex: number };
    wantInside: boolean;
  } | null {
    const project = this.ctx.history.state;
    const tol =
      hitRadiusFor(OFFSET_EDGE_TOL_PX, currentPointerKind()) /
      (scope.view?.zoom || 1);
    const q = { x: pt.x, y: pt.y };
    let best: {
      pieceId: string;
      sourcePathData: string;
      segment: { pathIndex: number; curveIndex: number };
      wantInside: boolean;
    } | null = null;
    let bestDist = Infinity;
    const consider = (
      pieceId: string,
      pathData: string,
      wantInside: boolean,
    ) => {
      const edge = nearestOutlineEdge(pathData, q, scope, tol);
      if (edge && edge.dist < bestDist) {
        bestDist = edge.dist;
        best = {
          pieceId,
          sourcePathData: pathData,
          segment: { pathIndex: edge.pathIndex, curveIndex: edge.curveIndex },
          wantInside,
        };
      }
    };
    const view = this.ctx.scene.getPartLayoutView();
    const scanPieces = (accepts: (pieceId: string) => boolean) => {
      for (const piece of project.pieces) {
        // Hidden parts don't catch an offset — hide the one on top to reach the
        // edge beneath it (same rule as the polyline first vertex).
        if (!isVisible(piece)) continue;
        if (!accepts(piece.id)) continue;
        // In Spread view a moved linked part is drawn `offset` from its model
        // geometry; move each source edge into displayed space so it hit-tests
        // against the displayed cursor and the whole offset drag runs in
        // displayed space (commitPoints maps the result back to model space) (#786).
        const offset = pieceDisplayOffset(piece, view);
        const toDisplay = (d: string) =>
          offset.dx !== 0 || offset.dy !== 0
            ? translatePathData(d, offset.dx, offset.dy, scope)
            : d;
        // Part outline → inset the seam into the part.
        consider(piece.id, toDisplay(piece.shapePathData), true);
        // Cutouts → offset the seam OUT into the leather around the hole.
        for (const cutout of piece.cutouts) {
          if (isVisible(cutout))
            consider(piece.id, toDisplay(cutout.pathData), false);
        }
        // Punched holes (position + radius) → synthesize the circle outline and
        // offset OUT into the leather.
        for (const hole of piece.holes) {
          if (!isVisible(hole)) continue;
          const displayCenter = {
            x: hole.position.x + offset.dx,
            y: hole.position.y + offset.dy,
          };
          const data = this.circlePathData(scope, displayCenter, hole.radius);
          if (data) consider(piece.id, data, false);
        }
      }
    };
    // #783: a selected part's edges WIN — an offset over stacked parts targets
    // the part you picked, not whichever edge is nearest on a part below.
    const selectedId = this.ctx.scene.getSelection().pieceId;
    if (selectedId) scanPieces((id) => id === selectedId);
    // #983: …but that must not strand the user on one part. Committing an offset
    // auto-selects the part it landed on, so without a fallback the next offset
    // on ANOTHER part found no edge at all — no hover affordance, and a press
    // did nothing, until the selection was cleared. When the cursor is nowhere
    // near the selected part's edges, scan every visible part instead. The
    // selected part still wins wherever both are in range, so #783 is intact.
    if (!best) scanPieces(() => true);
    return best;
  }

  /** A part's outline (`shapePathData`) translated into DISPLAYED space — the
   *  same space the offset seam points live in — so the seam can be clipped to
   *  the leather before committing (#780). Null when the part is gone. */
  private displayedPieceOutline(
    scope: paper.PaperScope,
    pieceId: string,
  ): string | null {
    const piece = this.ctx.history.state.pieces.find((p) => p.id === pieceId);
    if (!piece) return null;
    const offset = pieceDisplayOffset(
      piece,
      this.ctx.scene.getPartLayoutView(),
    );
    return offset.dx !== 0 || offset.dy !== 0
      ? translatePathData(piece.shapePathData, offset.dx, offset.dy, scope)
      : piece.shapePathData;
  }

  /** SVG pathData for a hole's circular outline (holes store position + radius,
   *  not a path) so it can drive the same edge-offset machinery. */
  private circlePathData(
    scope: paper.PaperScope,
    center: Vec2,
    radius: number,
  ): string | null {
    if (!(radius > 0)) return null;
    const circle = new scope.Path.Circle(
      new scope.Point(center.x, center.y),
      radius,
    );
    const data = circle.pathData;
    circle.remove();
    return data || null;
  }

  /** Advance an offset-from-edge drag: rebuild the parallel-seam preview at the
   *  current inset (drag distance or the typed Distance preset). */
  private updateOffsetDrag(s: PointerSample, p: { x: number; y: number }) {
    const od = this.offsetDrag;
    if (!od) return;
    const scope = this.ctx.scene.getPaperScope();
    this.lastOffsetSample = s;
    this.lastOffsetP = p;
    const built = this.buildOffsetSeam(scope, od, s.altKey, p);
    this.updateOffsetPreview(scope, od, built);
    this.showOffsetRay(scope, od, built);
  }

  /** Distance ray (#789): a vector from the grabbed edge's perpendicular foot
   *  out to the inset seam, labelled with the offset distance — mirroring the
   *  construction offset ray (#746). The `to` end is the nearest point on the
   *  built seam to the foot, so the ray reads the true perpendicular gap (the
   *  actual inset, whether drag- or Distance-preset-defined). Cleared when the
   *  drag is too small to build a seam. */
  private showOffsetRay(
    scope: paper.PaperScope,
    od: NonNullable<StitchTool["offsetDrag"]>,
    built: { points: Vec2[]; closed: boolean } | null,
  ): void {
    if (!built || built.points.length < 2) {
      this.ctx.scene.clearGuideRays();
      return;
    }
    const foot = nearestPointOnEdge(
      od.sourcePathData,
      od.segment,
      { x: od.startPt.x, y: od.startPt.y },
      scope,
    );
    if (!foot) {
      this.ctx.scene.clearGuideRays();
      return;
    }
    const seam = new scope.Path({
      segments: built.points.map((pt) => new scope.Point(pt.x, pt.y)),
    });
    seam.closed = built.closed;
    const footPt = new scope.Point(foot.x, foot.y);
    const near = seam.getNearestPoint(footPt);
    seam.remove();
    if (!near) {
      this.ctx.scene.clearGuideRays();
      return;
    }
    const dist = footPt.getDistance(near);
    if (dist < OFFSET_MIN_INSET_MM) {
      this.ctx.scene.clearGuideRays();
      return;
    }
    this.ctx.scene.setGuideRays([
      {
        from: { x: foot.x, y: foot.y },
        to: { x: near.x, y: near.y },
        lengthMm: dist,
      },
    ]);
  }

  /** Build the offset seam points for the current drag. The inset magnitude is
   *  the typed Distance preset when set, else the cursor's perpendicular
   *  distance from the grabbed edge (mirrored back into the panel input as it
   *  changes). The "single edge" toggle or a held Alt offsets just that edge
   *  (open); otherwise the whole outline (closed). Null when too small. */
  private buildOffsetSeam(
    scope: paper.PaperScope,
    od: NonNullable<StitchTool["offsetDrag"]>,
    altKey: boolean,
    p: { x: number; y: number },
  ): { points: Vec2[]; closed: boolean } | null {
    let inset = distanceToEdge(od.sourcePathData, od.segment, p, scope);
    if (inset == null) return null;
    const preset = this.presetOffsetDistance;
    if (preset !== null && isFinite(preset) && preset !== 0) {
      inset = Math.abs(preset);
    } else {
      // No preset — the drag defines the magnitude; echo it into the panel.
      this.liveOffsetDistanceCallback?.(inset);
    }
    if (inset < OFFSET_MIN_INSET_MM) return null;
    const wantSegment = this.singleSegmentOffset || altKey;
    // The FULL parallel seam (a single-edge offset overshoots the part's
    // corners). Cropping to the leather (#780) happens where the seam is
    // CONSUMED — the preview (`updateOffsetPreview`) and the commit
    // (`commitPoints`) — so the committed line keeps the full geometry as
    // `authoredPoints` and a later move can re-crop from it.
    return wantSegment
      ? offsetSegmentToPoints(
          od.sourcePathData,
          od.segSign * inset,
          od.segment,
          scope,
        )
      : offsetWholeToPoints(od.sourcePathData, od.wholeSign * inset, scope, {
          roundedCorners: this.offsetRoundedCorners,
        });
  }

  /** Crop a built offset seam to the part outline for DISPLAY (#780) — the
   *  preview should show what will actually commit. Returns the full seam
   *  unchanged if the outline is unknown or nothing lands inside. */
  private cropOffsetForDisplay(
    scope: paper.PaperScope,
    od: NonNullable<StitchTool["offsetDrag"]>,
    built: { points: Vec2[]; closed: boolean },
  ): { points: Vec2[]; closed: boolean } {
    if (!od.pieceOutlineData) return built;
    const clip = clipSeamToOutline(
      built.points,
      built.closed,
      od.pieceOutlineData,
      scope,
    );
    return clip ?? built;
  }

  /** Live preview for the offset drag — the parallel outline plus the chisel
   *  marks it will carry (mirrors the shape-drag preview). */
  private updateOffsetPreview(
    scope: paper.PaperScope,
    od: NonNullable<StitchTool["offsetDrag"]>,
    fullBuilt: { points: Vec2[]; closed: boolean } | null,
  ) {
    this.cleanupPreview();
    // Preview the seam AS IT WILL COMMIT — cropped to the leather (#780).
    const built = fullBuilt
      ? this.cropOffsetForDisplay(scope, od, fullBuilt)
      : null;
    if (!built || built.points.length < (built.closed ? 3 : 2)) return;
    const project = this.ctx.history.state;
    const piece = project.pieces.find((p) => p.id === od.pieceId);
    const eff = piece
      ? resolvePartStitchingDefaults(project, piece)
      : resolveProjectStitchingDefaults(project);
    const previewPitch = effectivePitchFromDefaults(eff);

    const group = new scope.Group();
    group.data = { ephemeral: true };

    const guide = new scope.Path({
      segments: built.points.map((p) => new scope.Point(p.x, p.y)),
    });
    guide.closed = built.closed;
    guide.strokeColor = new scope.Color(PREVIEW_STROKE());
    guide.strokeWidth = 0.6;
    guide.strokeScaling = false;
    guide.fillColor = null;
    group.addChild(guide);

    const previewLine: StitchLine = {
      id: "__preview__",
      points: built.points,
      pitch: eff.pitch,
      size: eff.size,
      shape: eff.shape,
      slantAngle: eff.slantAngle,
      pitchMode: eff.pitchMode,
      closed: built.closed,
    };
    for (const place of stitchHolePlacements(previewLine)) {
      const data = stitchHolePathData(
        place,
        eff.shape,
        eff.size,
        eff.slantAngle,
        previewPitch,
      );
      try {
        const cp = new scope.CompoundPath({ pathData: data, insert: false });
        cp.strokeColor = new scope.Color(PREVIEW_STROKE());
        cp.strokeWidth = 0.8;
        cp.strokeScaling = false;
        cp.fillColor = new scope.Color(0, 0, 0, 0.25);
        group.addChild(cp);
      } catch {
        /* ignore */
      }
    }
    this.previewItem = group;
  }

  /** Shift / Alt toggled mid shape-drag — replay the drag so square / circle
   *  and draw-from-centre update without waiting for the next mouse move. */
  override onModifiersChanged(modifiers: {
    shiftKey: boolean;
    ctrlKey: boolean;
    metaKey: boolean;
    altKey: boolean;
  }) {
    // Toggling Alt mid offset-drag switches whole-outline ↔ single-edge live.
    if (this.offsetDrag && this.lastOffsetSample && this.lastOffsetP) {
      this.updateOffsetDrag(
        { ...this.lastOffsetSample, ...modifiers },
        this.lastOffsetP,
      );
      return;
    }
    if (
      (!this.isClickKind() || this.regularPolygonDrag) &&
      this.shapeStart &&
      this.lastShapeSample &&
      this.lastShapeP
    ) {
      this.updateShapeDrag(
        { ...this.lastShapeSample, ...modifiers },
        this.lastShapeP,
      );
      return;
    }
    // Regular-polygon stage hover (#931/#934): Ctrl/Cmd toggles the angle
    // snap on the aimed / rubber-banded side live.
    if (
      this.kind === "polygon" &&
      this.polyGesture.stageActive &&
      this.lastStageHoverSample &&
      this.lastStageHoverP
    ) {
      this.polyGesture.hover(
        { ...this.lastStageHoverSample, ...modifiers },
        this.lastStageHoverP,
      );
    }
  }

  // ---- DrawGestureHost implementation (#804 IE2) --------------------------
  // StitchTool owns a `DrawGestureEngine` (`this.drawEngine`) that runs the
  // generic click-to-add polyline / polygon flow. Construction-only caps are
  // off; the leather vertex/angle/grid snap, the working-area clamp, the live
  // chisel preview (`buildPreview`), and the StitchLine commit are injected.

  getScope(): paper.PaperScope {
    return this.ctx.scene.getPaperScope();
  }
  getScene(): Scene {
    return this.ctx.scene;
  }
  previewColor(): string {
    return PREVIEW_STROKE();
  }
  isPolygon(): boolean {
    return this.kind === "polygon";
  }
  isPolyKind(): boolean {
    return (
      this.kind === "polyline" ||
      this.kind === "polygon" ||
      this.kind === "line"
    );
  }
  /** DrawGestureHost: a Line is done once its one segment exists (#1113). */
  finishesAfterOneSegment(): boolean {
    return this.kind === "line";
  }
  /** Stitch has no line continuation (#328). */
  get continuationEnabled(): boolean {
    return false;
  }
  /** Stitch has no per-vertex bezier handles (#217). */
  get bezierEnabled(): boolean {
    return false;
  }
  seedContinuation(): boolean {
    return false;
  }
  /** Stitch previews don't light a mid-point self-snap halo. */
  selfSnapAt(): { pos: paper.Point; index: number } | null {
    return null;
  }

  /** DrawGestureHost: resolve a placed vertex through the leather snap tiers
   *  (a nearby existing vertex wins, else angle snap from the previous vertex,
   *  else grid, else the raw point) and clamp it inside the anchored part so
   *  the seam never leaves the leather. The first vertex's part is anchored in
   *  `onDown` before delegation. Extracted from the old `onDown` polyline path. */
  resolveDraftPlacement(
    s: PointerSample,
    p: { x: number; y: number },
  ): paper.Point {
    const scope = this.ctx.scene.getPaperScope();
    const vsnap = this.snap(p);
    let snapped: paper.Point;
    if (vsnap.hit?.kind === "vertex") {
      snapped = new scope.Point(vsnap.point.x, vsnap.point.y);
      setAngleHint(null);
    } else if (this.shouldSnap(s) && this.drawEngine.points.length > 0) {
      const last = this.drawEngine.points[this.drawEngine.points.length - 1]!;
      const sn = snapToCommonAngle(last, p);
      snapped = new scope.Point(sn.x, sn.y);
      setAngleHint(sn.angleRad);
    } else {
      snapped = new scope.Point(vsnap.point.x, vsnap.point.y);
    }
    // Clamp to the active part's outline — keeps every vertex on or inside the
    // leather even when the user clicks past the edge.
    return this.clampToActivePiece(scope, snapped);
  }

  /** DrawGestureHost: resolve the rubber-band hover through the same snap tiers
   *  and working-area clamp, so the preview shows exactly where the next vertex
   *  would land. Extracted from the old `onHover` polyline path. */
  resolveDraftHover(
    s: PointerSample,
    p: { x: number; y: number },
  ): paper.Point {
    const scope = this.ctx.scene.getPaperScope();
    const vsnap = this.snap(p);
    let pt = new scope.Point(p.x, p.y);
    if (vsnap.hit?.kind === "vertex") {
      pt = new scope.Point(vsnap.point.x, vsnap.point.y);
      setAngleHint(null);
    } else if (this.shouldSnap(s)) {
      const last = this.drawEngine.points[this.drawEngine.points.length - 1]!;
      const sn = snapToCommonAngle(last, p);
      pt = new scope.Point(sn.x, sn.y);
      setAngleHint(sn.angleRad);
    } else {
      if (vsnap.hit?.kind === "line" || vsnap.hit?.kind === "grid")
        pt = new scope.Point(vsnap.point.x, vsnap.point.y);
      setAngleHint(null);
    }
    return this.clampToActivePiece(scope, pt);
  }

  /** DrawGestureHost: `onHover` while the draft is empty. This tool routes empty
   *  hovers itself (see `onHover`), so this is a defensive mirror of that path. */
  onEmptyDraftHover(_s: PointerSample, p: { x: number; y: number }): void {
    this.prePressHover(this.ctx.scene.getPaperScope(), p);
  }

  /** DrawGestureHost: commit the in-progress polyline / polygon draft. Snapshots
   *  the engine's points, resets the draft, then builds + commits the StitchLine
   *  (#804 IE2). */
  commitDraft(closed: boolean) {
    const points = this.drawEngine.points.map((p) => ({ x: p.x, y: p.y }));
    this.cancelDraft();
    this.commitPoints(points, closed);
  }

  /** Build and commit a StitchLine from explicit points. Shared by the engine
   *  draft commit (`commitDraft`), the offset-from-edge drag (#768), and the
   *  drag-out shape kinds — each samples its geometry to points then funnels
   *  here (they do NOT run through the engine). Owner-part resolution (#783),
   *  the stitching defaults snapshot, naming, and selection all live here.
   *
   *  `forcedPieceId` pins the owning part instead of resolving it from the
   *  seam geometry. The offset gesture passes the part it started on so the
   *  seam always belongs there — an offset from a cutout that overshoots the
   *  leather edge must NOT re-home onto a part lying underneath (the
   *  overshoot is then simply cropped away against that same source part). */
  private commitPoints(
    rawPoints: Vec2[],
    closed: boolean = false,
    forcedPieceId?: string,
    /** Bezier handles for `rawPoints` (#982) — set by the curved drag-out
     *  kinds, which store their real geometry rather than a sampling of it. */
    handles?: SeamHandle[],
    /** The freehand trace + its smoothness (#1016), kept so the seam can be
     *  re-derived at a different smoothness later. */
    freehand?: { rawPoints: Vec2[]; smoothness: number },
  ) {
    const points = rawPoints.map((p) => ({ x: p.x, y: p.y }));
    this.cleanupPreview();
    this.ctx.scene.setDrawSnapPoint(null);
    this.ctx.scene.clearGuideRays();
    setAngleHint(null);
    if (points.length < 2) return;
    // A closed loop needs at least three vertices to enclose any area; with
    // two it'd just back-track on itself. Guard here as well as at the call
    // site so the model invariant holds regardless of caller.
    const isClosed = closed && points.length >= 3;

    // Pick the part the seam belongs to. With `forcedPieceId` the owner is
    // fixed (the offset gesture's source part, #768); otherwise resolve it
    // (#783): never hidden, prefer the selected part, else the top-most
    // visible part whose outline contains the first vertex — with the seam's
    // bounding box as the overlap fallback.
    const scope = this.ctx.scene.getPaperScope();
    const project = this.ctx.history.state;
    let pieceId: string;
    let offset: { dx: number; dy: number };
    if (forcedPieceId != null) {
      const forced = project.pieces.find((p) => p.id === forcedPieceId);
      if (!forced) return;
      pieceId = forcedPieceId;
      offset = pieceDisplayOffset(forced, this.ctx.scene.getPartLayoutView());
    } else {
      const xs = points.map((p) => p.x);
      const ys = points.map((p) => p.y);
      const seamBounds = {
        x: Math.min(...xs),
        y: Math.min(...ys),
        width: Math.max(...xs) - Math.min(...xs),
        height: Math.max(...ys) - Math.min(...ys),
      };
      const target = resolveDrawTarget(
        project,
        points[0]!,
        scope,
        this.ctx.scene.getSelection().pieceId,
        this.ctx.scene.getPartLayoutView(),
        seamBounds,
      );
      if (!target) return;
      pieceId = target.pieceId;
      offset = target.offset;
    }
    const piece = project.pieces.find((p) => p.id === pieceId);
    // Crop the drawn seam to the part outline so no hole lands off the leather
    // (#780). Runs in DISPLAYED space (where `points` were collected), against
    // the part outline shifted into that same space. A seam wholly inside is a
    // no-op; an overshooting one keeps its full geometry as `authoredPoints` so
    // a later move can re-crop from it.
    const displayedOutline =
      piece != null
        ? offset.dx !== 0 || offset.dy !== 0
          ? translatePathData(piece.shapePathData, offset.dx, offset.dy, scope)
          : piece.shapePathData
        : null;
    const seam =
      displayedOutline != null
        ? cropSeamForPiece(points, isClosed, displayedOutline, scope, handles)
        : { points, closed: isClosed, handles };
    // Store the seam in the part's ORIGINAL space so the spread render translate
    // doesn't double-offset it (#786). `points` were collected in displayed
    // space (clamped against the displayed outline).
    const toOrig = (pts: Vec2[]) =>
      offset.dx !== 0 || offset.dy !== 0
        ? pts.map((p) => toOriginalSpace(p, offset))
        : pts;
    // Snapshot the part's effective stitching defaults — falls back through
    // piece → project → hardcoded fallback. Subsequent edits to those
    // defaults cascade into this line via the cascade-on-write helper.
    const eff = piece
      ? resolvePartStitchingDefaults(project, piece)
      : resolveProjectStitchingDefaults(project);
    // An OFFSET seam (forcedPieceId) traces the part's tilted outline, so its
    // points are already rotated with the part. Inherit the part's rotation
    // frame so the seam's transform bbox starts tilted the same way (#971) — it
    // hugs, and a later resize/rotate runs skew-free. A hand-drawn seam is left
    // un-tilted (its points are axis-aligned as authored, not part-aligned).
    const inheritRotation =
      forcedPieceId != null &&
      piece?.rotation !== undefined &&
      piece.rotationCenter
        ? { rotation: piece.rotation, rotationCenter: piece.rotationCenter }
        : {};
    const line: StitchLine = {
      id: newId("stitch"),
      name: piece ? nextStitchName(project) : undefined,
      points: toOrig(seam.points),
      // Handles are RELATIVE to their anchors, so the display→original shift is
      // a translation they ride along unchanged (#982).
      handles: seam.handles,
      // The trace makes the same display→original trip as the points (#1016).
      ...(freehand
        ? {
            rawPoints: toOrig(freehand.rawPoints),
            smoothness: freehand.smoothness,
          }
        : {}),
      pitch: eff.pitch,
      size: eff.size,
      shape: eff.shape,
      slantAngle: eff.slantAngle,
      pitchMode: eff.pitchMode,
      startOffset: 0,
      overridesDefaults: false,
      closed: (seam.closed && seam.points.length >= 3) || undefined,
      authoredPoints: seam.authoredPoints
        ? toOrig(seam.authoredPoints)
        : undefined,
      authoredClosed: seam.authoredClosed,
      ...newSeamLayoutDefaults(),
      ...inheritRotation,
    };
    // Seeded like a new construction line (#1099) — a traced seam's curved
    // anchors start locked smooth, so the outline editor never has to guess.
    const seedAnchors = computeDefaultSmoothAnchors(
      seamPathData(line),
      this.ctx.scene.getPaperScope(),
    );
    if (seedAnchors) line.smoothAnchors = seedAnchors;
    this.snapTargets = null; // geometry changed — rebuild targets next use
    this.ctx.commit("Add stitch line", (proj) =>
      withStitchLine(proj, pieceId, line),
    );
    this.ctx.setSelection({
      pieceId,
      decorationId: null,
      stitchId: line.id,
    });
  }
}

function setCanvasCursor(scope: paper.PaperScope, cursor: string) {
  const el = scope.view?.element as HTMLCanvasElement | undefined;
  if (el) el.style.cursor = cursor;
}
